// src/components/CreateBlank.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useToast } from 'helpers/hooks';
import styles from 'css_modules/Upload.module.css';

/**
 * Generates a minimal 1-page blank PDF file directly in browser memory.
 */
const createBlankPdfFile = (fileName = 'blanknewdocument.pdf') => {
  const blankPdfContent = `%PDF-1.4
1 0 obj
<< /Type /Catalog /Pages 2 0 R >>
endobj
2 0 obj
<< /Type /Pages /Kids [3 0 R] /Count 1 >>
endobj
3 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources <<>> >>
endobj
xref
0 4
0000000000 65535 f 
0000000009 00000 n 
0000000058 00000 n 
0000000115 00000 n 
trailer
<< /Size 4 /Root 1 0 R >>
startxref
206
%%EOF`;

  const blob = new Blob([blankPdfContent], { type: 'application/pdf' });
  return new File([blob], fileName, { type: 'application/pdf' });
};

export const CreateBlank = ({ isMobile, onMenuClick }) => {
  const [error, setError] = useState(null);
  const showToast = useToast();
  const history = useHistory();
  const hasTriggered = useRef(false);

  useEffect(() => {
    // Prevent duplicate triggers in React Strict Mode
    if (hasTriggered.current) return;
    hasTriggered.current = true;

    const handleAutoCreateBlankDocument = async () => {
      try {
        // 1. Create file payload
        const blankFile = createBlankPdfFile('blanknewdocument.pdf');
        const formData = new FormData();
        formData.append('document_file', blankFile);

        // 2. Direct upload call using app API base URL and JWT token
        const uploadUrl = process.env.REACT_APP_API + '/upload/';
        const token = localStorage.getItem('DocspointJWT');

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': 'JWT ' + token
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        const data = await response.json();

        // 3. Handle success and redirect directly to detail page
        if (data && data.uuid) {
          showToast('Nový prázdný doklad byl úspěšně vytvořen');
          history.push(`/my_documents/${data.uuid}/general`);
        } else {
          throw new Error('UUID missing in server response');
        }
      } catch (err) {
        console.error('Failed to create blank document:', err);
        setError('Nepodařilo se vytvořit nový doklad.');
        showToast('Chyba při vytváření nového dokladu');
      }
    };

    handleAutoCreateBlankDocument();
  }, [history, showToast]);

  return (
    <>
      <div className={styles.heading}>
        {isMobile && (
          <button
            onClick={onMenuClick}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginRight: '1rem',
              width: '28px',
              height: '28px'
            }}
            aria-label="Open navigation menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--primary, #007bff)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}
        Vytvořit nový doklad
      </div>

      <div className={styles.dropzoneWrapper}>
        <section className={`${styles.wrapper} ${styles.fullPage}`}>
          <div
            className={styles.dropzoneBox}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.5rem',
              padding: '2rem'
            }}
          >
            {!error ? (
              <span className={styles.dropzoneCaption}>
                Vytvářím nový doklad, prosím čekejte...
              </span>
            ) : (
              <p className={styles.errorMessage}>{error}</p>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default CreateBlank;